import React, { useState } from 'react';
import { Alert, Button, Form, Input, Typography } from 'antd';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { useNavigate } from 'react-router-dom';
import PasswordStrengthValidator, { PasswordValidation } from '../Login/PasswordStrengthValidator';
import '../../style/LoginForm.css';

interface NewPassFormProps {
    token: string;
    userId: string;
}

const NewPassForm: React.FC<NewPassFormProps> = ({ token, userId }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [passwordValidation, setPasswordValidation] = useState<PasswordValidation | null>(null);
    const [newPassword, setNewPassword] = useState<string>('');
    const navigate = useNavigate();
    const { Title, Text } = Typography;

    const handleSubmit = async (values: { newPassword: string; confirmPassword: string }) => {
        setError(null);
        setSuccess(null);

        if (!passwordValidation?.isValid) {
            setError('Please ensure your password meets all security requirements before resetting.');
            return;
        }

        setIsLoading(true);
        try {
            const endpoint = `${ENDPOINTS.RESET_PASSWORD}/${token}/${userId}`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ newPassword: values.newPassword }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Failed to reset password' }));
                throw new Error(errorData?.message || 'Failed to reset password');
            }

            setSuccess('Password reset successfully. Redirecting to login...');
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (submitError: any) {
            setError(submitError?.message || 'Failed to reset password. Please try again or request a new reset link.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            className="login-form reset-password-form auth-antd-form"
            onFinish={handleSubmit}
            autoComplete="on"
        >
            <div className="auth-form-header">
                <Title level={2}>Reset Password</Title>
                <Text className="auth-form-subtitle">
                    Enter your new password below.
                </Text>
            </div>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    message={error}
                    className="auth-form-alert"
                />
            )}

            {success && (
                <Alert
                    type="success"
                    showIcon
                    message={success}
                    className="auth-form-alert"
                />
            )}

            <Form.Item
                name="newPassword"
                rules={[
                    { required: true, message: 'Please enter your new password.' },
                    {
                        validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            if (!passwordValidation?.isValid) {
                                return Promise.reject(new Error('Password does not meet security requirements.'));
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
                className="auth-form-item"
            >
                <Input.Password
                    placeholder="New password"
                    autoComplete="new-password"
                    size="large"
                    disabled={isLoading}
                    onChange={(e) => setNewPassword(e.target.value)}
                />
            </Form.Item>

            {newPassword && (
                <div className="password-validator-wrapper">
                    <PasswordStrengthValidator
                        password={newPassword}
                        onValidationChange={setPasswordValidation}
                        showRequirements={true}
                    />
                </div>
            )}

            <Form.Item
                name="confirmPassword"
                dependencies={['newPassword']}
                rules={[
                    { required: true, message: 'Please confirm your new password.' },
                    ({ getFieldValue }) => ({
                        validator(_, value) {
                            if (!value || getFieldValue('newPassword') === value) {
                                return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords do not match.'));
                        },
                    }),
                ]}
                className="auth-form-item"
            >
                <Input.Password
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    size="large"
                    disabled={isLoading}
                />
            </Form.Item>

            <Form.Item className="auth-form-item auth-submit-item">
                <Button
                    className="button auth-submit-button"
                    type="primary"
                    htmlType="submit"
                    loading={isLoading}
                    block
                    size="large"
                    disabled={!!newPassword && !passwordValidation?.isValid}
                >
                    {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </Button>
            </Form.Item>

            <div className="auth-form-footer">
                <Button type="link" className="link-like-button" onClick={() => navigate('/login')}>
                    Back to Login
                </Button>
            </div>
        </Form>
    );
};

export default NewPassForm;
